// JSON-like tree structure for routes
export interface RouteNode {
  path: string;
  name: string;
  component?: string;
  children?: RouteNode[];
  meta?: {
    layout?: 'auth' | 'main';
    protected?: boolean;
    legacy?: boolean;
    redirect?: string;
    icon?: string;
    description?: string;
    group?: string;
    allowSecretKey?: boolean;
    hideFloatingChat?: boolean;
  };
}

// Complete route tree in JSON-like structure
export const routeTree: RouteNode = {
  path: '/',
  name: 'root',
  children: [
    // App base - redirect to dashboard
    {
      path: '/app',
      name: 'App Home',
      meta: {
        layout: 'main',
        protected: true,
        redirect: '/app/dashboard',
        description: 'Redirect base /app to dashboard'
      }
    },
    // OAuth Callback Route (Root with secret_key parameter)
    {
      path: '/',
      name: 'OAuth Callback',
      component: 'SalesforceCallback',
      meta: {
        layout: 'auth',
        protected: true,
        allowSecretKey: true, // Allow access with secret_key parameter
        description: 'Salesforce OAuth callback handler'
      }
    },

    // Authentication Routes (Public)
    {
      path: '/auth',
      name: 'Authentication',
      meta: {
        layout: 'auth',
        protected: false,
        group: 'auth'
      },
      children: [
        {
          path: '/auth/login',
          name: 'Login',
          component: 'Login',
          meta: {
            layout: 'auth',
            protected: false,
            description: 'Sign in to your account'
          }
        }
      ]
    },

    // Main Application Routes (Protected)
    {
      path: '/app/dashboard',
      name: 'Dashboard',
      component: 'Dashboard',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Home',
        description: 'Main dashboard with overview and stats',
        group: 'main'
      }
    },
    {
      path: '/profile',
      name: 'Profile',
      component: 'Profile',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'User',
        description: 'Manage your account settings and preferences',
        group: 'account'
      }
    },
    {
      path: '/slack-link',
      name: 'Link Slack',
      component: 'SlackLink',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Link',
        description: 'Link your Slack account to ncoder (weave)',
        group: 'account'
      }
    },
    {
      path: '/ai',
      name: 'AI Assistant',
      component: 'AIAssistant',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Bot',
        description: 'Get intelligent help with your Salesforce development',
        group: 'main'
      }
    },
    {
      path: '/modernise',
      name: 'Modernise with AI',
      component: 'ModerniseWithAI',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Sparkles',
        description: 'Upgrade your legacy Salesforce code with AI assistance',
        group: 'main'
      }
    },

    // Metadata V1 Route (now at /metadata)
    {
      path: '/metadata',
      name: 'Metadata',
      component: 'MetadataV1',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Database',
        description: 'Metadata page',
        group: 'metadata'
      }
    },

// {
//   path: '/dev-agent',
//   name: 'Chat',
//   component: 'DevAgent',
//   meta: {
//     layout: 'main',
//     protected: true,
//     icon: 'Bot',
//     description: 'Autonomous dev agent with task planning, live tool execution, and approval workflow',
//     group: 'main'
//   }
// },

    // SOQL & Data Tools Routes
    {
      path: '/soql',
      name: 'SOQL & Data',
      meta: {
        layout: 'main',
        protected: true,
        group: 'data'
      },
      children: [
        {
          path: '/soql/builder',
          name: 'Query Builder',
          component: 'QueryBuilder',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'Search',
            description: 'Build and execute SOQL queries'
          }
        },
        {
          path: '/soql/export',
          name: 'Data Export',
          component: 'DataExport',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'Download',
            description: 'Export Salesforce data to various formats'
          }
        },
        {
          path: '/soql/import',
          name: 'Data Import',
          component: 'DataImport',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'Upload',
            description: 'Import data into Salesforce from various sources'
          }
        },
        {
          path: '/soql/fields',
          name: 'Field Inspector',
          component: 'FieldInspector',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'Search',
            description: 'Inspect and analyze field metadata'
          }
        },
        {
          path: '/soql/objects',
          name: 'Object Inspector',
          component: 'ObjectInspector',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'Database',
            description: 'Inspect and analyze object metadata'
          }
        }
      ]
    },

    // Deployment & DevOps Routes
    {
      path: '/deployment',
      name: 'Deployment',
      meta: {
        layout: 'main',
        protected: true,
        group: 'devops'
      },
      children: [
        {
          path: '/deployment/deploy',
          name: 'Deploy (Legacy)',
          meta: {
            layout: 'main',
            protected: true,
            legacy: true,
            redirect: '/deploy'
          }
        },
        {
          path: '/deployment/retrieve',
          name: 'Retrieve',
          component: 'Retrieve',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'Download',
            description: 'Retrieve metadata from your Salesforce org'
          }
        },
        {
          path: '/deployment/package-builder',
          name: 'Package Builder',
          component: 'PackageBuilder',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'Package',
            description: 'Build deployment packages for your metadata'
          }
        },
        {
          path: '/deployment/file/:fileId',
          name: 'File Details',
          component: 'FileDetails',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'FileCode',
            description: 'View file details and changes'
          }
        }
      ]
    },

    // Security & Quality Routes
    {
      path: '/security',
      name: 'Security',
      meta: {
        layout: 'main',
        protected: true,
        group: 'security'
      },
      children: [
        {
          path: '/security/scanner',
          name: 'Security Scanner',
          component: 'SecurityScanner',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'Shield',
            description: 'Scan your code for security vulnerabilities',
            hideFloatingChat: true,
          }
        }
      ]
    },

    // AI Agent Development Routes
    {
      path: '/migrations',
      name: 'Migrations',
      component: 'TemplatesRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'BookOpen',
        description: 'Browse the migration catalog',
        hideFloatingChat: true,
      }
    },
    {
      path: '/migrations/new',
      name: 'Create Migration',
      component: 'PlaybookCreateRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Plus',
        description: 'Create a reusable migration',
        hideFloatingChat: true,
      }
    },
    {
      path: '/tools',
      name: 'Tools',
      component: 'SkillsConnectorsRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'BookOpen',
        description: 'Browse the skills catalog',
        hideFloatingChat: true,
      }
    },
    {
      path: '/skills',
      name: 'Skills',
      meta: {
        layout: 'main',
        protected: true,
        legacy: true,
        redirect: '/migrations'
      }
    },
    {
      path: '/migrations/:id',
      name: 'Migration Workspace',
      component: 'TemplateWorkspaceRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'BookOpen',
        description: 'Open a single migration workspace',
        hideFloatingChat: true,
      }
    },
    {
      path: '/skills/:id',
      name: 'Skill Workspace',
      component: 'TemplateWorkspaceRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'BookOpen',
        description: 'Open a single migration workspace',
        hideFloatingChat: true,
      }
    },
    {
      path: '/migrations/:id/run',
      name: 'Run Migration',
      component: 'TemplateRunRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Play',
        description: 'Run a migration and stream the outcome',
        hideFloatingChat: true,
      }
    },
    {
      path: '/skills/:id/run',
      name: 'Run Migration',
      component: 'TemplateRunRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Play',
        description: 'Run a migration and stream the outcome',
        hideFloatingChat: true,
      }
    },
    // Migration ENGINE — deterministic metadata migrations, separate from the playbook
    // path above. Both pages gate themselves on a runtime probe of the engine API, so
    // in a deployment without one they render an explanation and touch nothing else.
    // Deliberately NOT under /migrations/*: that space already has a `/migrations/:id`
    // wildcard, and a static sibling there would be one refactor away from ambiguity.
    {
      path: '/migration-engine',
      name: 'Migration Engine',
      component: 'MigrationEngineRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'GitCompareArrows',
        description: 'Deterministic metadata migrations: coverage, residue, manual steps',
        hideFloatingChat: true,
      }
    },
    // Org-wide automation triage. NOT `/migration-engine/portfolio`: that space already
    // has a `:planId` wildcard, and a static sibling under it would rank correctly today
    // and ambiguously after the next refactor — the same reason the engine was kept out
    // of `/migrations/*`. The page is self-gating: free inventory for every org, the
    // paid pass disabled with a contact path where it is not entitled.
    {
      path: '/migration-portfolio',
      name: 'Portfolio Pass',
      component: 'MigrationPortfolioRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Layers',
        description: 'Every WorkflowRule in this org, triaged into the shapes it falls into',
        hideFloatingChat: true,
      }
    },
    {
      path: '/migration-engine/:planId',
      name: 'Migration Engine Plan',
      component: 'MigrationEnginePlanRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'GitCompareArrows',
        description: 'One migration plan: what was carried, what was dropped, and why',
        hideFloatingChat: true,
      }
    },
    {
      path: '/runs',
      name: 'Runs',
      component: 'RunsRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'History',
        description: 'Migration run history',
        hideFloatingChat: true,
      }
    },
    {
      path: '/reports',
      name: 'Reports',
      component: 'ReportsRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'FileText',
        description: 'Generated report history and downloads',
        hideFloatingChat: true,
      }
    },
    {
      path: '/agents',
      name: 'Agents',
      component: 'AgentsRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Bot',
        description: 'Browse queryable and actionable agents',
        hideFloatingChat: true,
      }
    },
    {
      path: '/agents/new',
      name: 'Create Agent',
      component: 'AgentCreateRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Plus',
        description: 'Create an agent from a name and job description',
        hideFloatingChat: true,
      }
    },
    {
      path: '/agents/:id',
      name: 'Agent Workspace',
      component: 'AgentWorkspaceRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Bot',
        description: 'Open a single agent workspace',
        hideFloatingChat: true,
      }
    },
    {
      path: '/agents/:id/run',
      name: 'Run Agent',
      component: 'AgentRunRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Play',
        description: 'Run an agent and stream the report',
        hideFloatingChat: true,
      }
    },
    {
      path: '/salesforce-releases',
      name: 'Salesforce Releases',
      component: 'SalesforceReleasesRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'ShieldCheck',
        description: 'Run Salesforce release scans and download findings',
        hideFloatingChat: true,
      }
    },
    {
      path: '/release-scanner',
      name: 'Release Scanner',
      component: 'SalesforceReleasesRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'ShieldCheck',
        description: 'Run Salesforce release scans and download findings',
        hideFloatingChat: true,
      }
    },
    {
      path: '/testing',
      name: 'Testing',
      component: 'TestingRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'ListChecks',
        description: 'Plan, approve, and run end-to-end tests in a scratch org',
        hideFloatingChat: true,
      }
    },
    {
      path: '/testing/analytics',
      name: 'Testing Analytics',
      component: 'TestingAnalyticsRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'BarChart2',
        description: 'Test analytics and pass rate trends',
        hideFloatingChat: true,
      }
    },
    /*
     * Observability moved into the Admin panel as a tab, at product's request.
     *
     * A REDIRECT, not a deletion. This was a top-level route for long enough to be bookmarked and
     * pasted into chats, and a bookmark to it carries no `?tab=` — so `adminTabs`'s legacy map, which
     * translates values of that parameter, cannot help. The router is the only layer that can keep
     * the link working, and `<Navigate replace>` also stops the dead path accumulating history
     * entries as the reader goes back.
     *
     * `component` is deliberately absent: `generator.ts` skips a node with neither a component nor a
     * redirect, so the two fields are the only thing that makes this node do anything.
     */
    {
      path: '/observability',
      name: 'Observability (moved to Admin panel)',
      meta: {
        layout: 'main',
        protected: true,
        legacy: true,
        redirect: '/studio/org-admin?tab=observability',
      }
    },
    {
      path: '/debugging',
      name: 'Salesforce Debugger',
      component: 'DebuggingRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Bug',
        description: 'Analyze Salesforce debug logs, governor limits, call flow, and runtime failures',
        hideFloatingChat: true,
      }
    },
    {
      path: '/scheduling',
      name: 'Scheduling',
      component: 'SchedulingRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'CalendarClock',
        description: 'Schedule agent reports and email delivery',
        hideFloatingChat: true,
      }
    },
    // Primary routes at /weave (new brand name for AI with Weave)
    {
      path: '/weave',
      name: 'Weave',
      component: 'MultiAgentRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Network',
        description: 'AI with Weave — multi-agent Salesforce analysis sessions',
        hideFloatingChat: true,
      }
    },
    // Weave 2: the VS Code extension's own webview over the cloud session store (additive; does not touch /weave)
    {
      path: '/weave2',
      name: 'Weave 2',
      component: 'Weave2Route',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Network',
        description: 'Weave 2 — the VS Code extension UI, on sessions shared with VS Code',
        hideFloatingChat: true,
      }
    },
    {
      path: '/weave/classic',
      name: 'AI with Weave · Classic layout',
      component: 'MultiAgentLayoutClassic',
      meta: { layout: 'main', protected: true, icon: 'Network', description: 'AI with Weave — classic 4-column layout', hideFloatingChat: true }
    },
    {
      path: '/weave/focus',
      name: 'AI with Weave · Focus layout',
      component: 'MultiAgentLayoutFocus',
      meta: { layout: 'main', protected: true, icon: 'Network', description: 'AI with Weave — focused centered reading layout', hideFloatingChat: true }
    },
    {
      path: '/weave/command',
      name: 'AI with Weave · Command layout',
      component: 'MultiAgentLayoutCommand',
      meta: { layout: 'main', protected: true, icon: 'Network', description: 'AI with Weave — mission-control layout', hideFloatingChat: true }
    },
    {
      path: '/weave/split',
      name: 'AI with Weave · Split layout',
      component: 'MultiAgentLayoutSplit',
      meta: { layout: 'main', protected: true, icon: 'Network', description: 'AI with Weave — answer/code split layout', hideFloatingChat: true }
    },
    {
      path: '/weave/zen',
      name: 'AI with Weave · Zen layout',
      component: 'MultiAgentLayoutZen',
      meta: { layout: 'main', protected: true, icon: 'Network', description: 'AI with Weave — full-bleed zen layout', hideFloatingChat: true }
    },
    // Backward-compat redirects: /multi-agent/* → /weave/*
    {
      path: '/multi-agent',
      name: 'AI with Weave (redirect)',
      meta: { layout: 'main', protected: true, legacy: true, redirect: '/weave' }
    },
    {
      path: '/multi-agent/classic',
      name: 'AI with Weave Classic (redirect)',
      meta: { layout: 'main', protected: true, legacy: true, redirect: '/weave/classic' }
    },
    {
      path: '/multi-agent/focus',
      name: 'AI with Weave Focus (redirect)',
      meta: { layout: 'main', protected: true, legacy: true, redirect: '/weave/focus' }
    },
    {
      path: '/multi-agent/command',
      name: 'AI with Weave Command (redirect)',
      meta: { layout: 'main', protected: true, legacy: true, redirect: '/weave/command' }
    },
    {
      path: '/multi-agent/split',
      name: 'AI with Weave Split (redirect)',
      meta: { layout: 'main', protected: true, legacy: true, redirect: '/weave/split' }
    },
    {
      path: '/multi-agent/zen',
      name: 'AI with Weave Zen (redirect)',
      meta: { layout: 'main', protected: true, legacy: true, redirect: '/weave/zen' }
    },
    {
      path: '/org360',
      name: 'Org 360',
      component: 'Org360Route',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Bot',
        description: 'Explore object graph, fields, triggers, and impact',
        hideFloatingChat: true,
      }
    },
    {
      path: '/explorer',
      name: 'Explorer',
      component: 'Org360Route',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Bot',
        description: 'Explore object graph, fields, triggers, and impact',
        hideFloatingChat: true,
      }
    },
    {
      path: '/documents',
      name: 'Documents',
      meta: {
        layout: 'main',
        protected: true,
        legacy: true,
        redirect: '/docs'
      }
    },
    {
      path: '/documentation',
      name: 'Documentation',
      meta: {
        layout: 'main',
        protected: true,
        legacy: true,
        redirect: '/docs'
      }
    },
    {
      path: '/version-control',
      name: 'Version Control',
      meta: {
        layout: 'main',
        protected: true,
        legacy: true,
        redirect: '/git'
      }
    },
    {
      path: '/studio/metadata',
      name: 'Studio Metadata Explorer',
      component: 'StudioMetadataRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Database',
        description: 'Studio-native metadata graph route',
        hideFloatingChat: true,
      }
    },
    {
      path: '/studio/deployment',
      name: 'Studio Deployment',
      component: 'StudioDeploymentRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Layers',
        description: 'Studio-native deployment route',
        hideFloatingChat: true,
      }
    },
    {
      path: '/studio/git',
      name: 'Studio Git Pipelines (Legacy)',
      meta: {
        layout: 'main',
        protected: true,
        legacy: true,
        redirect: '/git'
      }
    },
    {
      path: '/studio/security',
      name: 'Studio Security Scanner',
      component: 'StudioSecurityRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Shield',
        description: 'Studio-native security scanner route',
        hideFloatingChat: true,
      }
    },
    {
      path: '/studio/docs',
      name: 'Studio Documentation',
      component: 'StudioDocsRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'FileText',
        description: 'Studio-native documentation artifacts route'
      }
    },
    {
      path: '/studio/org-admin',
      name: 'Studio Org Admin',
      component: 'StudioOrgAdminRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Settings',
        description: 'Org admin panel rebuilt on the org RBAC & invitations REST API',
        hideFloatingChat: true,
      }
    },
    {
      // Stripe Checkout's success/cancel URL (docs/billing-api.md). Must exist or a completed
      // purchase lands on a 404 after the customer has already paid. Renders nothing — it forwards
      // into the Billing tab, which owns the settling banner and the polling.
      path: '/billing/return',
      name: 'Billing Return',
      component: 'BillingReturn',
      meta: {
        layout: 'main',
        protected: true,
        description: 'Stripe Checkout return handler — forwards into the admin Billing tab',
        hideFloatingChat: true,
      }
    },
    {
      path: '/studio/connection',
      name: 'Studio Connection Manager',
      component: 'StudioConnectionRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Link',
        description: 'Studio-native connection and tools route'
      }
    },
    {
      path: '/agent',
      name: 'AI Agent',
      meta: {
        layout: 'main',
        protected: true,
        group: 'agent'
      },
      children: [
        {
          path: '/agent/training',
          name: 'Training Data',
          component: 'TrainingData',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'Database',
            description: 'Manage training data for your AI agents'
          }
        },
        {
          path: '/agent/testing',
          name: 'Agent Testing',
          component: 'AgentTesting',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'TestTube',
            description: 'Test and validate your AI agents',
            hideFloatingChat: true,
          }
        },
        {
          path: '/agent/deployment',
          name: 'Agent Deployment',
          component: 'AgentDeployment',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'Layers',
            description: 'Deploy AI agents to your Salesforce org',
            hideFloatingChat: true,
          }
        },
        {
          path: '/agent/knowledge',
          name: 'Knowledge Base',
          component: 'KnowledgeBase',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'BookOpen',
            description: 'Manage knowledge base for your AI agents'
          }
        },
        {
          path: '/agent/voice',
          name: 'Voice Configuration',
          component: 'VoiceConfig',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'Mic',
            description: 'Configure voice settings for your AI agents'
          }
        }
      ]
    },

    // Organization Management Routes
    {
      path: '/org',
      name: 'Organization',
      meta: {
        layout: 'main',
        protected: true,
        group: 'organization'
      },
      children: [
        {
          path: '/org/connection',
          name: 'Connection Manager',
          component: 'ConnectionManager',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'Network',
            description: 'Manage your Salesforce org connections'
          }
        },
        {
          path: '/org/auth',
          name: 'Authentication',
          component: 'Authentication',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'Key',
            description: 'Manage authentication settings for your org'
          }
        }
      ]
    },

    // Support & Documentation Routes
    {
      path: '/support',
      name: 'Support',
      meta: {
        layout: 'main',
        protected: true,
        group: 'support'
      },
      children: [
        {
          path: '/support/help-center',
          name: 'Help Center',
          component: 'HelpCenter',
          meta: {
            layout: 'main',
            protected: true,
            icon: 'HelpCircle',
            description: 'Find answers and get support'
          }
        }
      ]
    },

    // Additional Support Routes (top-level for navigation)
    {
      path: '/team',
      name: 'Team Collaboration',
      component: 'TeamCollaboration',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Users',
        description: 'Collaborate with your team members',
        group: 'support'
      }
    },
    {
      path: '/help',
      name: 'Help & Support',
      component: 'HelpSupport',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'HelpCircle',
        description: 'Get help and support for nCoder AI',
        group: 'support'
      }
    },
    {
      path: '/docs',
      name: 'Documentation',
      component: 'Documentation',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'FileText',
        description: 'Browse the complete documentation',
        group: 'support'
      }
    },
    {
      path: '/release-notes',
      name: 'Release Notes',
      component: 'ReleaseNotes',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Megaphone',
        description: "See what's new, changed, and deprecated in nCoder.ai",
        group: 'support'
      }
    },
    {
      path: '/user-guide',
      name: 'User Guide',
      component: 'UserGuide',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'BookOpen',
        description: 'Step-by-step procedures for using nCoder.ai yourself',
        group: 'support'
      }
    },
    {
      path: '/text-to-soql',
      name: 'Text to SOQL',
      component: 'TextToSOQL',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'MessageSquareCode',
        description: 'Convert natural language to SOQL queries',
        group: 'data'
      }
    },
    {
      path: '/upgrade',
      name: 'Upgrade',
      component: 'Upgrade',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Crown',
        description: 'Upgrade your nCoder AI plan',
        group: 'account'
      }
    },
    {
      path: '/deploy',
      name: 'Deploy',
      component: 'Deploy',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'Upload',
        description: 'Deploy metadata to your Salesforce org',
        group: 'devops',
        hideFloatingChat: true,
      }
    },
    {
      path: '/git',
      name: 'Git & Pipelines',
      component: 'GitRoute',
      meta: {
        layout: 'main',
        protected: true,
        icon: 'GitBranch',
        description: 'Connect a repository and run pipeline playbooks with org context',
        group: 'devops',
        hideFloatingChat: true,
      }
    },

    // Legacy Routes (for backward compatibility)
    {
      path: '/login',
      name: 'Login (Legacy)',
      meta: {
        layout: 'auth',
        protected: false,
        legacy: true,
        redirect: '/auth/login'
      }
    },
    {
      path: '/soql',
      name: 'SOQL (Legacy)',
      meta: {
        layout: 'main',
        protected: true,
        legacy: true,
        redirect: '/soql/builder'
      }
    },
    {
      path: '/deployment',
      name: 'Deployment (Legacy)',
      meta: {
        layout: 'main',
        protected: true,
        legacy: true,
        redirect: '/deployment/deploy'
      }
    },
    {
      path: '/security',
      name: 'Security (Legacy)',
      meta: {
        layout: 'main',
        protected: true,
        legacy: true,
        redirect: '/security/scanner'
      }
    },

    // Development Routes (kept for reference)
    {
      path: '/home',
      name: 'Home (Dev)',
      component: 'HomePage',
      meta: {
        layout: 'main',
        protected: true,
        description: 'Original home page for reference',
        group: 'dev'
      }
    },
    {
      path: '/about',
      name: 'About (Dev)',
      component: 'AboutPage',
      meta: {
        layout: 'main',
        protected: true,
        description: 'About page for reference',
        group: 'dev'
      }
    },

    // Catch-all route
    {
      path: '*',
      name: 'Not Found',
      component: 'NotFound',
      meta: {
        layout: 'main',
        protected: true,  // Changed: 404 page should be protected
        description: 'Page not found'
      }
    }
  ]
};

// Utility functions to work with the tree structure
export function flattenRoutes(node: RouteNode, prefix = ''): RouteNode[] {
  const routes: RouteNode[] = [];
  
  if (node.component || node.meta?.redirect) {
    routes.push({
      ...node,
      path: prefix + node.path
    });
  }
  
  if (node.children) {
    for (const child of node.children) {
      routes.push(...flattenRoutes(child, prefix));
    }
  }
  
  return routes;
}

export function getRoutesByGroup(group: string): RouteNode[] {
  const allRoutes = flattenRoutes(routeTree);
  return allRoutes.filter(route => route.meta?.group === group);
}

export function getProtectedRoutes(): RouteNode[] {
  const allRoutes = flattenRoutes(routeTree);
  return allRoutes.filter(route => route.meta?.protected !== false);
}

export function getPublicRoutes(): RouteNode[] {
  const allRoutes = flattenRoutes(routeTree);
  return allRoutes.filter(route => route.meta?.protected === false);
}

export function getLegacyRoutes(): RouteNode[] {
  const allRoutes = flattenRoutes(routeTree);
  return allRoutes.filter(route => route.meta?.legacy === true);
}

export function getRouteByPath(path: string): RouteNode | undefined {
  const allRoutes = flattenRoutes(routeTree);
  return allRoutes.find(route => route.path === path);
}

// Generate navigation config from route tree
export function generateNavigationFromTree(): any[] {
  const groups = new Map();
  const allRoutes = flattenRoutes(routeTree);
  
  allRoutes.forEach(route => {
    if (route.meta?.group && !route.meta?.legacy && route.component) {
      const group = route.meta.group;
      if (!groups.has(group)) {
        groups.set(group, []);
      }
      groups.get(group).push({
        id: route.path.replace(/[\/\-]/g, '_'),
        title: route.name,
        url: route.path,
        icon: route.meta.icon,
        description: route.meta.description
      });
    }
  });
  
  return Array.from(groups.entries()).map(([groupName, items]) => ({
    id: groupName,
    title: groupName.charAt(0).toUpperCase() + groupName.slice(1),
    items
  }));
}
